import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Space, Spin, Table, Tag, Typography } from 'antd';
import { RegulationReaderBody } from './RegulationViews';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import { groupByTopic } from '../../utils/regulatoryAdminModel';
import { isQuietNotFound, MATCH_COLORS, MATCH_LABELS, PAGE_SIZE, buildApplicableQuery, documentTypeLabel } from '../../utils/regulatoryModel';

const { Text } = Typography;

/**
 * "Which authorities apply?" for one project (Lead Advisor / Advisor). The defaults and their reasons come from
 * the API (derived from the company record); ticking boxes only changes the query's `authorities` override.
 * Nothing is saved: reloading the page returns to the server's defaults.
 */
const ApplicableRegulationsPanel = ({ projectId }) => {
    const { request } = useApiRequest();
    const [override, setOverride] = useState(null); // null = server defaults, else array of authority names
    const [includeNonMatching, setIncludeNonMatching] = useState(false);
    const [offset, setOffset] = useState(0);
    const [selectedId, setSelectedId] = useState(null);

    const nothingChosen = Array.isArray(override) && override.length === 0;
    const res = useResource(
        async () => request('GET', `/projects/${encodeURIComponent(projectId)}/applicable-regulations`, { query: buildApplicableQuery({ override, includeNonMatching, offset }) }),
        [request, projectId, override, includeNonMatching, offset],
        { enabled: !nothingChosen },
    );

    const data = res.data?.data;
    // Kept across loads so the boxes stay (and can be re-ticked) while no authority is selected and nothing is fetched.
    const [lastSelection, setLastSelection] = useState([]);
    const [defaults, setDefaults] = useState([]);
    useEffect(() => {
        if (data?.authoritySelection) setLastSelection(data.authoritySelection);
        // The server's defaults are read from the first (override-free) load only.
        if (data?.authoritySelection && override === null) setDefaults((d) => (d.length === 0 ? data.authorities || [] : d));
    }, [data, override]);
    const selection = lastSelection;
    const items = data?.items || [];
    const total = res.data?.meta?.total ?? items.length;
    const current = override ?? defaults;

    const toggle = (authority, on) => {
        const base = override ?? defaults;
        setOffset(0);
        setSelectedId(null);
        setOverride(on ? [...new Set([...base, authority])] : base.filter((a) => a !== authority));
    };

    const columns = [
        { title: 'Title', key: 'title', render: (_v, r) => <Text>{r.title}</Text> },
        { title: 'Authority', key: 'authority', render: (_v, r) => r.authorityName },
        { title: 'Type', key: 'type', render: (_v, r) => documentTypeLabel(r.documentType) },
        { title: 'Issued', key: 'issued', render: (_v, r) => formatDdMmYyyy(r.publishedAt) },
        { title: 'Applies?', key: 'match', render: (_v, r) => <Tag color={MATCH_COLORS[r.applicabilityMatch]}>{MATCH_LABELS[r.applicabilityMatch] || r.applicabilityMatch}</Tag> },
        { title: '', key: 'open', render: (_v, r) => <Button onClick={() => setSelectedId(r.id)}>View</Button> },
    ];
    const selected = items.find((i) => i.id === selectedId);
    const grouped = Array.isArray(data?.byTopic) ? groupByTopic(data.byTopic, data.unclassifiedItemIds, items) : null;

    return (
        <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                These are suggestions derived from the company's profile. Change the selection to look at other authorities; nothing here is saved to the project.
            </Text>

            {res.status === 'error' && isQuietNotFound(res.error) && <Empty description="No regulatory information is available for this project." />}
            {res.status === 'error' && !isQuietNotFound(res.error) && <Alert type="error" style={{ marginBottom: 12 }} message={userMessageForError(res.error)} action={<Button onClick={res.reload}>Retry</Button>} />}
            {res.status === 'loading' && !res.data && <Spin />}

            {selection.length > 0 && (
                <div style={{ marginBottom: 12 }} data-testid="authority-selection">
                    {selection.map((s) => (
                        <div key={s.authority} style={{ marginBottom: 4 }}>
                            <Checkbox checked={current.includes(s.authority)} onChange={(e) => toggle(s.authority, e.target.checked)}>
                                {s.authority}
                            </Checkbox>
                            <Text type="secondary" style={{ fontSize: 12 }}> {s.reason}</Text>
                        </div>
                    ))}
                </div>
            )}
            {nothingChosen && <Alert type="info" style={{ marginBottom: 12 }} message="Choose at least one authority to see its regulations." />}
            <div style={{ marginBottom: 12 }}>
                <Checkbox checked={includeNonMatching} onChange={(e) => { setOffset(0); setIncludeNonMatching(e.target.checked); }}>
                    Also show documents that do not match this company's industry
                </Checkbox>
                {override && <Button style={{ marginLeft: 8 }} onClick={() => { setOverride(null); setOffset(0); }}>Reset to suggested</Button>}
            </div>

            {res.data && items.length === 0 && !nothingChosen && <Empty description="No published regulations for the selected authorities yet." />}
            {res.data && items.length > 0 && !nothingChosen && (
                <>
                    <Table columns={columns} dataSource={items} rowKey={(r) => r.id} pagination={false} size="small" />
                    <Space style={{ marginTop: 8 }}>
                        <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
                        <Text type="secondary">{offset + 1}-{offset + items.length} of {total}</Text>
                        <Button disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
                    </Space>
                </>
            )}
            {grouped && (grouped.domains.length > 0 || grouped.unclassified.count > 0) && !nothingChosen && (
                <div style={{ marginTop: 16 }} data-testid="topics-apply">
                    <Text strong>Topics that apply to this project</Text>
                    {grouped.domains.map((d) => (
                        <div key={d.id} style={{ marginTop: 8 }} data-testid="topic-domain">
                            <Text strong>{d.title}</Text>
                            <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>
                                {d.topics.map((t) => (
                                    <li key={t.id} data-testid="topic-group">
                                        {t.title} <Text type="secondary">({t.count} document{t.count === 1 ? '' : 's'}{t.names.length ? `: ${t.names.join('; ')}` : ''}{t.offPage ? `${t.names.length ? '; ' : ': '}${t.offPage} on other pages` : ''})</Text>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {grouped.unclassified.count > 0 && (
                        <div style={{ marginTop: 8 }} data-testid="topic-unclassified">
                            <Text strong>Not yet classified</Text>{' '}
                            <Text type="secondary">({grouped.unclassified.count} document{grouped.unclassified.count === 1 ? '' : 's'}{grouped.unclassified.names.length ? `: ${grouped.unclassified.names.join('; ')}` : ''}{grouped.unclassified.offPage ? `${grouped.unclassified.names.length ? '; ' : ': '}${grouped.unclassified.offPage} on other pages` : ''})</Text>
                        </div>
                    )}
                </div>
            )}
            {selected && (
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginTop: 16 }} data-testid="applicable-detail">
                    <Space style={{ marginBottom: 8 }}><Text strong>{selected.title}</Text><Tag color={MATCH_COLORS[selected.applicabilityMatch]}>{MATCH_LABELS[selected.applicabilityMatch] || selected.applicabilityMatch}</Tag><Button onClick={() => setSelectedId(null)}>Close</Button></Space>
                    <RegulationReaderBody item={selected} />
                </div>
            )}
        </div>
    );
};

export default ApplicableRegulationsPanel;
